import React, { useState } from 'react';

const BANK_PARTNERS = [
  { id: 'ICICI', name: 'ICICI Bank' },
  { id: 'KOTAK', name: 'Kotak Mahindra Bank' },
  { id: 'HDFC', name: 'HDFC Bank' },
  { id: 'SBI', name: 'State Bank of India' }
];

const SUBSCRIPTION_TIERS = {
  STARTER: { name: 'Starter', seatCap: 4, label: 'Starter (4 Seats)' },
  ENTERPRISE: { name: 'Enterprise', seatCap: 10, label: 'Enterprise (10 Seats)' }
};

export default function AgencyOnboardingPortal() {
  const [formData, setFormData] = useState({
    agencyName: '',
    bankPartner: 'ICICI',
    subscriptionTier: 'STARTER',
    managerEmail: '',
    draCertificateHolder: ''
  });

  const [provisionedTenant, setProvisionedTenant] = useState(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const generateSecureApiKey = () => {
    const array = new Uint8Array(24);
    window.crypto.getRandomValues(array);
    const hex = Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
    return `debtone_live_sec_${hex}`;
  };

  const handleProvisionTenant = (e) => {
    e.preventDefault();
    setIsProvisioning(true);

    setTimeout(() => {
      const tenantId = `tnt_${formData.bankPartner.toLowerCase()}_${Date.now().toString(36)}`;
      const apiKey = generateSecureApiKey();
      const tierConfig = SUBSCRIPTION_TIERS[formData.subscriptionTier];

      const newTenant = {
        tenantId,
        apiKey,
        agencyName: formData.agencyName,
        bankPartner: formData.bankPartner,
        tier: tierConfig.name,
        seatCap: tierConfig.seatCap,
        managerEmail: formData.managerEmail,
        draCertificateHolder: formData.draCertificateHolder || `${formData.agencyName} Compliance Lead`,
        status: 'ACTIVE',
        endpointBinding: `https://api.debtone.io/v1/tenants/${tenantId}/rls`,
        createdAt: new Date().toISOString()
      };

      setProvisionedTenant(newTenant);
      setIsProvisioning(false);
    }, 1200);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <header style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#0F172A', margin: '0 0 8px 0' }}>
          Agency Manager Onboarding Portal
        </h1>
        <p style={{ color: '#475569', margin: 0 }}>
          Provision new bank agency tenants, allocate operational seat caps, and issue isolated RLS API keys.
        </p>
      </header>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        {/* Registration Form */}
        <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: '8px', border: '1fr solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1E293B', marginTop: 0, marginBottom: '20px' }}>
            Tenant Provisioning Form
          </h2>
          <form onSubmit={handleProvisionTenant}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>
                Agency Name
              </label>
              <input
                type="text"
                name="agencyName"
                value={formData.agencyName}
                onChange={handleInputChange}
                required
                placeholder="e.g. Alpha Recovery Services"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>
                Bank Partner
              </label>
              <select
                name="bankPartner"
                value={formData.bankPartner}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '14px', boxSizing: 'border-box' }}
              >
                {BANK_PARTNERS.map((bank) => (
                  <option key={bank.id} value={bank.id}>
                    {bank.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>
                Subscription Tier
              </label>
              <select
                name="subscriptionTier"
                value={formData.subscriptionTier}
                onChange={handleInputChange}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '14px', boxSizing: 'border-box' }}
              >
                <option value="STARTER">{SUBSCRIPTION_TIERS.STARTER.label}</option>
                <option value="ENTERPRISE">{SUBSCRIPTION_TIERS.ENTERPRISE.label}</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>
                Manager Email Address
              </label>
              <input
                type="email"
                name="managerEmail"
                value={formData.managerEmail}
                onChange={handleInputChange}
                required
                placeholder="manager@agency.com"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#334155', marginBottom: '6px' }}>
                Official DRA Certificate Holder
              </label>
              <input
                type="text"
                name="draCertificateHolder"
                value={formData.draCertificateHolder}
                onChange={handleInputChange}
                placeholder="Primary Certificate Representative"
                style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #CBD5E1', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>

            <button
              type="submit"
              disabled={isProvisioning}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: isProvisioning ? '#94A3B8' : '#2563EB',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: isProvisioning ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.2s'
              }}
            >
              {isProvisioning ? 'Provisioning Infrastructure...' : 'Provision Agency Tenant'}
            </button>
          </form>
        </div>

        {/* Status Card & Provisioning Output */}
        <div>
          {provisionedTenant ? (
            <div style={{ backgroundColor: '#FFFFFF', padding: '24px', borderRadius: '8px', border: '1px solid #10B981', boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#065F46', margin: 0 }}>
                  Active Tenant Provisioned
                </h2>
                <span style={{ backgroundColor: '#D1FAE5', color: '#065F46', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
                  {provisionedTenant.status}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
                <div style={{ padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '4px' }}>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '12px' }}>TENANT ID</span>
                  <strong style={{ color: '#0F172A', fontFamily: 'monospace' }}>{provisionedTenant.tenantId}</strong>
                </div>

                <div style={{ padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '4px' }}>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '12px' }}>AGENCY & PARTNER</span>
                  <strong style={{ color: '#0F172A' }}>{provisionedTenant.agencyName} ({provisionedTenant.bankPartner})</strong>
                </div>

                <div style={{ padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '4px' }}>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '12px' }}>SEAT CAP ALLOCATION</span>
                  <strong style={{ color: '#0F172A' }}>{provisionedTenant.tier} Tier — {provisionedTenant.seatCap} Allocated Seats</strong>
                </div>

                <div style={{ padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '4px' }}>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '12px' }}>DRA CERTIFICATE HOLDER MAPPING</span>
                  <strong style={{ color: '#0F172A' }}>{provisionedTenant.draCertificateHolder}</strong>
                </div>

                <div style={{ padding: '8px 12px', backgroundColor: '#F8FAFC', borderRadius: '4px' }}>
                  <span style={{ color: '#64748B', display: 'block', fontSize: '12px' }}>RLS ENDPOINT BINDING</span>
                  <strong style={{ color: '#0F172A', fontFamily: 'monospace', wordBreak: 'break-all' }}>{provisionedTenant.endpointBinding}</strong>
                </div>

                <div style={{ padding: '12px', backgroundColor: '#EFF6FF', borderRadius: '6px', border: '1px solid #BFDBFE' }}>
                  <span style={{ color: '#1E40AF', display: 'block', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
                    SECURE LIVE API KEY (SAVE IMMEDIATELY)
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <code style={{ fontSize: '12px', color: '#1E3A8A', wordBreak: 'break-all', flex: 1 }}>
                      {provisionedTenant.apiKey}
                    </code>
                    <button
                      onClick={() => copyToClipboard(provisionedTenant.apiKey)}
                      style={{ padding: '6px 10px', fontSize: '12px', backgroundColor: '#2563EB', color: '#FFF', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      {copiedKey ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ backgroundColor: '#F8FAFC', padding: '40px 24px', borderRadius: '8px', border: '2px dashed #CBD5E1', textAlign: 'center' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#64748B', margin: '0 0 8px 0' }}>
                No Active Tenant Provisioned
              </h3>
              <p style={{ fontSize: '14px', color: '#94A3B8', margin: 0 }}>
                Complete and submit the registration form to provision an isolated tenant environment, allocate seats, and generate API keys.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}